import useSWR, { mutate } from 'swr';
import { useEffect, useMemo } from 'react';

import { useDispatch } from 'react-redux';
import axiosInstance, { endpoints, fetcher } from 'src/utils/axios';
import { IBetHighlightAmount, IMasterController, ITemplate } from 'src/types/settings';
import { ERROR_MSG, SWR_CONFIG } from 'src/config-global';
import { setBetHighlightAmount } from 'src/redux/reducers/currency-slice';
import type { IGroup } from 'src/types/group';

export interface ChangePasswordData {
  role_name: 'transfer' | 'void' | 'rollback';
  current_password: number;
  new_password: number;
  confirm_password: number;
}

export interface TUpdateBetHighlightAmount {
  amount: number;
  currency_id: string;
  setting_id: string;
}

//   export interface ApiResponse {
//     success: boolean;
//     message: string;
//     // Add other fields as necessary based on your API response structure
//   }

const URL = endpoints.settings.master_controller;
const TEMPLATESURL = endpoints.settings.templates;
const betHighlightAmountURL = endpoints.settings.betHighlightAmount;
const PASSCODEURL = endpoints.settings.change_passcode;

// Template Groups API endpoints
const TEMPLATE_GROUPS_URL = (templateId: string) => endpoints.settings.templateGroups(templateId);
const TEMPLATE_GROUP_URL = (templateId: string, groupId: string) => endpoints.settings.templateGroup(templateId, groupId);
const TEMPLATE_GROUP_GAME_EVENT_SETTINGS_URL = (templateId: string, groupId: string, gameEventId: string) => 
  endpoints.settings.templateGroupGameEventSettings(templateId, groupId, gameEventId);

export function useGetMainMasterController() {
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {...SWR_CONFIG});
  const memoizedValue = useMemo(
    () => ({
      masterControls: (data?.data as IMasterController[]) || [],
      masterControlsLoading: isLoading,
      masterControlsError: error,
      masterControlsValidating: isValidating,
      masterControlsEmpty: !isLoading && !data?.data,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updateMainMasterController(data: any) {
  try {
    await axiosInstance.put(endpoints.settings.updateStatus, data);
    mutate(URL);
  } catch (error) {
    if (error && error.status_message) throw error.status_message;
    else throw ERROR_MSG;
  }
}

export function useGetTemplates() {
  const { data, isLoading, error, isValidating } = useSWR(TEMPLATESURL, fetcher,{...SWR_CONFIG});
  const memoizedValue = useMemo(
    () => ({
      templates: (data?.data as ITemplate[]) || [],
      templatesCount: data?.data?.length,
      templatesLoading: isLoading,
      templatesError: error,
      templatesValidating: isValidating,
      templatesEmpty: !isLoading && !data?.data,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetTemplate(templateId: string | undefined) {
  const url = templateId ? `${TEMPLATESURL}/${templateId}` : null;
  const { data, isLoading, error, isValidating } = useSWR(url, fetcher, {...SWR_CONFIG});
  
  const memoizedValue = useMemo(
    () => ({
      template: data?.data as ITemplate | undefined,
      templateLoading: isLoading,
      templateError: error,
      templateValidating: isValidating,
      templateEmpty: !isLoading && !data?.data,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
export const updateChangePasscode = async (data: ChangePasswordData) => {
  try {
    const response = await axiosInstance.put(PASSCODEURL, data);
    return response;
  } catch (error) {
    if (error && error.status_message) throw error.status_message;
    else throw ERROR_MSG;
  }
  // if (!response.response_error ===true) {
  //   const errorData = await response;
  //   throw new Error(errorData.message || 'Failed to change password');
  // }

  // const responseData: ApiResponse = await response.json();
  // return responseData;
};

export function useBetHighlightAmount() {
  const dispatch = useDispatch();
  const { data, isLoading, error, isValidating } = useSWR(betHighlightAmountURL, fetcher,{...SWR_CONFIG});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reFetch = () => {
    mutate(betHighlightAmountURL);
  };

  useEffect(() => {
    const list = Array.isArray(data?.data?.bet_highlight_amount) ? data?.data?.bet_highlight_amount : [];
    dispatch(setBetHighlightAmount(list));

  }, [dispatch, data?.data?.bet_highlight_amount]);

  const memoizedValue = useMemo(() => {
    const list = Array.isArray(data?.data?.bet_highlight_amount) ? data?.data?.bet_highlight_amount : [];

    return {
      betHighlightAmount: (list as IBetHighlightAmount[]) || [],
      betHighlightAmountCount: list?.length,
      betHighlightAmountLoading: isLoading,
      betHighlightAmountError: error,
      betHighlightAmountValidating: isValidating,
      betHighlightAmountEmpty: !isLoading && (!list || list.length === 0),
      data: data?.data,
      reFetch,
    };
  }, [error, isLoading, isValidating, reFetch, data?.data]);

  return memoizedValue;
}

export const updateBetHighlightAmount = async (data: TUpdateBetHighlightAmount) => {
  try {
    const response = await axiosInstance.put(`${betHighlightAmountURL}/${data.setting_id}`, data);
    mutate(betHighlightAmountURL);
    return response;
  } catch (error) {
    if (error && error.status_message) throw error.status_message;
    else throw ERROR_MSG;
  }
};

// ----------------------------------------------------------------------
// Template Groups API (placeholder - update endpoints when API is ready)
// ----------------------------------------------------------------------

export function useGetTemplateGroups(templateId: string | undefined) {
  const url = templateId ? TEMPLATE_GROUPS_URL(templateId) : null;
  const { data, isLoading, error, isValidating } = useSWR(url, fetcher, {...SWR_CONFIG});
  
  const memoizedValue = useMemo(() => {
    // API returns: template_id, template_name, groups (with group_id, group_name, game_events_count, is_active), group_ids
    const responseData = data?.data || {};
    const groupsData = responseData.groups || responseData.group_ids || [];
    const groupIds = Array.isArray(groupsData)
      ? groupsData.map((g: any) => (typeof g === 'string' ? g : g.group_id || g))
      : [];
    
    return {
      templateId: responseData.template_id as string | undefined,
      templateName: responseData.template_name as string | undefined,
      groups: (responseData.groups || []) as IGroup[], // Full group objects if available
      templateGroups: groupIds as string[],
      templateGroupsLoading: isLoading,
      templateGroupsError: error,
      templateGroupsValidating: isValidating,
      templateGroupsEmpty: !isLoading && groupIds.length === 0,
    };
  }, [data?.data, error, isLoading, isValidating]);

  return memoizedValue;
}

export async function assignGroupToTemplate(templateId: string, groupId: string) {
  try {
    // TODO: Update endpoint when API is ready
    const { data } = await axiosInstance.post(TEMPLATE_GROUPS_URL(templateId), { group_id: groupId });
    mutate(TEMPLATE_GROUPS_URL(templateId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}

export async function removeGroupFromTemplate(templateId: string, groupId: string) {
  try {
    const { data } = await axiosInstance.delete(TEMPLATE_GROUP_URL(templateId, groupId));
    mutate(TEMPLATE_GROUPS_URL(templateId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}

// ----------------------------------------------------------------------
// Template Group Details API
// ----------------------------------------------------------------------

export type GetTemplateGroupParams = {
  sub_provider_name?: string;
};

export function useGetTemplateGroup(
  templateId: string | undefined,
  groupId: string | undefined,
  params?: GetTemplateGroupParams
) {
  const url = useMemo(() => {
    if (!templateId || !groupId) return null;
    const base = TEMPLATE_GROUP_URL(templateId, groupId);
    if (!params?.sub_provider_name) return base;
    const searchParams = new URLSearchParams();
    searchParams.set('sub_provider_name', params.sub_provider_name);
    return `${base}?${searchParams.toString()}`;
  }, [templateId, groupId, params?.sub_provider_name]);

  const { data, isLoading, error, isValidating } = useSWR(url, fetcher, {...SWR_CONFIG});
  
  const memoizedValue = useMemo(
    () => ({
      templateGroup: data?.data as IGroup | undefined, // Includes template overrides merged in
      templateGroupLoading: isLoading,
      templateGroupError: error,
      templateGroupValidating: isValidating,
      templateGroupEmpty: !isLoading && !data?.data,
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------
// Template-Level Game Event Settings API
// ----------------------------------------------------------------------

export type UpdateTemplateGameEventSettingsPayload = {
  game_thumbnail?: string | null;
  game_thumbnail2?: string | null;
  desktop_sequence?: number | null;
  mobile_sequence?: number | null;
  is_popular?: boolean;
  is_desktop_visibility?: boolean;
  is_mobile_visibility?: boolean;
  is_bet_lock?: boolean;
};

export async function updateTemplateGameEventSettings(
  templateId: string,
  groupId: string,
  gameEventId: string,
  payload: UpdateTemplateGameEventSettingsPayload
) {
  try {
    const { data } = await axiosInstance.patch(
      TEMPLATE_GROUP_GAME_EVENT_SETTINGS_URL(templateId, groupId, gameEventId),
      payload
    );
    // Mutate both template groups list and specific template group
    mutate(TEMPLATE_GROUPS_URL(templateId));
    mutate(TEMPLATE_GROUP_URL(templateId, groupId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}

// ----------------------------------------------------------------------
// Template-Level Game Event Management API
// ----------------------------------------------------------------------

export type AssignTemplateGameEventsPayload = {
  game_event_id?: string;
  sub_provider_name?: string;
};

export type RemoveTemplateGameEventsPayload = {
  game_event_id?: string;
  sub_provider_name?: string;
};

export async function assignTemplateGameEvents(
  templateId: string,
  groupId: string,
  payload: AssignTemplateGameEventsPayload
) {
  try {
    const { data } = await axiosInstance.post(
      endpoints.settings.templateGroupGameEventsAssign(templateId, groupId),
      payload
    );
    // Mutate template group to refresh game events list
    mutate(TEMPLATE_GROUP_URL(templateId, groupId));
    mutate(TEMPLATE_GROUPS_URL(templateId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}

export async function removeTemplateGameEvents(
  templateId: string,
  groupId: string,
  payload: RemoveTemplateGameEventsPayload
) {
  try {
    const { data } = await axiosInstance.post(
      endpoints.settings.templateGroupGameEventsRemove(templateId, groupId),
      payload
    );
    // Mutate template group to refresh game events list
    mutate(TEMPLATE_GROUP_URL(templateId, groupId));
    mutate(TEMPLATE_GROUPS_URL(templateId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}

// ----------------------------------------------------------------------
// Business Groups API (business inherits template groups; can override game selection)
// ----------------------------------------------------------------------

const BUSINESS_GROUPS_URL = (businessId: string) => endpoints.business.businessGroups(businessId);
const BUSINESS_GROUP_URL = (businessId: string, groupId: string) =>
  endpoints.business.businessGroup(businessId, groupId);

export function useGetBusinessGroups(businessId: string | undefined) {
  const url = businessId ? BUSINESS_GROUPS_URL(businessId) : null;
  const { data, isLoading, error, isValidating } = useSWR(url, fetcher, { ...SWR_CONFIG });

  const memoizedValue = useMemo(() => {
    const responseData = data?.data ?? data;
    const groupIds = responseData?.group_ids ?? [];
    const groups = responseData?.groups ?? [];
    return {
      businessGroups: Array.isArray(groupIds) ? groupIds : [],
      businessGroupsData: Array.isArray(groups) ? groups : [],
      businessGroupsLoading: isLoading,
      businessGroupsError: error,
      businessGroupsValidating: isValidating,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}

export type GetBusinessGroupParams = {
  search?: string;
  provider_name?: string;
  sub_provider_name?: string;
  category?: string;
};

/** Build full URL for GET business group (used for SWR cache key / mutate). */
export function getBusinessGroupUrl(
  businessId: string,
  groupId: string,
  params?: GetBusinessGroupParams
): string {
  const base = BUSINESS_GROUP_URL(businessId, groupId);
  if (!params || Object.keys(params).length === 0) return base;
  const searchParams = new URLSearchParams();
  if (params.search) searchParams.set('search', params.search);
  if (params.provider_name) searchParams.set('provider_name', params.provider_name);
  if (params.sub_provider_name) searchParams.set('sub_provider_name', params.sub_provider_name);
  if (params.category) searchParams.set('category', params.category);
  const qs = searchParams.toString();
  return qs ? `${base}?${qs}` : base;
}

function buildBusinessGroupUrl(
  businessId: string,
  groupId: string,
  params?: GetBusinessGroupParams
): string {
  return getBusinessGroupUrl(businessId, groupId, params);
}

export function useGetBusinessGroup(
  businessId: string | undefined,
  groupId: string | undefined,
  params?: GetBusinessGroupParams
) {
  const url =
    businessId && groupId
      ? buildBusinessGroupUrl(businessId, groupId, params)
      : null;
  const { data, isLoading, error, isValidating } = useSWR(url, fetcher, { ...SWR_CONFIG });

  // Support both shapes: { data: { ... } } or { business_id, group_id, group_name, game_events, game_events_count }
  const businessGroupData = data?.data ?? data;

  const memoizedValue = useMemo(
    () => ({
      businessGroup: businessGroupData as IGroup | undefined,
      businessGroupLoading: isLoading,
      businessGroupError: error,
      businessGroupValidating: isValidating,
      businessGroupEmpty: !isLoading && !businessGroupData,
    }),
    [businessGroupData, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export type AssignBusinessGameEventsPayload = {
  game_event_id?: string;
  sub_provider_name?: string;
};

export type RemoveBusinessGameEventsPayload = {
  game_event_id?: string;
  sub_provider_name?: string;
};

export async function assignBusinessGameEvents(
  businessId: string,
  groupId: string,
  payload: AssignBusinessGameEventsPayload
) {
  try {
    const { data } = await axiosInstance.post(
      endpoints.business.businessGroupGameEventsAssign(businessId, groupId),
      payload
    );
    mutate(BUSINESS_GROUP_URL(businessId, groupId));
    mutate(BUSINESS_GROUPS_URL(businessId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}

export async function removeBusinessGameEvents(
  businessId: string,
  groupId: string,
  payload: RemoveBusinessGameEventsPayload
) {
  try {
    const { data } = await axiosInstance.post(
      endpoints.business.businessGroupGameEventsRemove(businessId, groupId),
      payload
    );
    mutate(BUSINESS_GROUP_URL(businessId, groupId));
    mutate(BUSINESS_GROUPS_URL(businessId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}

export type UpdateBusinessGameEventSettingsPayload = {
  game_thumbnail?: string | null;
  game_thumbnail2?: string | null;
  desktop_sequence?: number | null;
  mobile_sequence?: number | null;
  is_popular?: boolean;
  is_desktop_visibility?: boolean;
  is_mobile_visibility?: boolean;
  is_bet_lock?: boolean;
};

export async function updateBusinessGameEventSettings(
  businessId: string,
  groupId: string,
  gameEventId: string,
  payload: UpdateBusinessGameEventSettingsPayload
) {
  try {
    const { data } = await axiosInstance.patch(
      endpoints.business.businessGroupGameEventSettings(businessId, groupId, gameEventId),
      payload
    );
    mutate(BUSINESS_GROUP_URL(businessId, groupId));
    mutate(BUSINESS_GROUPS_URL(businessId));
    return data;
  } catch (error: any) {
    if (error?.status_message) throw error.status_message;
    throw ERROR_MSG;
  }
}
